import { useAnimals, useLots, useRegisterBatchEvent, type AnimalSummary } from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { DateInput } from '@/components/ui/DateInput';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { getErrorMessage } from '@/lib/api';
import { BATCH_EVENT_TYPES } from '@/lib/batchEventTypes';
import { MILKING_SHIFT_OPTIONS } from '@/lib/dairy';
import { formatDate, formatWeight, todayIso } from '@/lib/format';
import { ArrowDown, ArrowUp, ClipboardList } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type BatchRow = {
  animal: AnimalSummary;
  weight: string;
  included: boolean;
};

function daysSince(date: string | null | undefined): number | null {
  if (!date) return null;
  const then = new Date(date);
  const now = new Date();
  const diff = Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : null;
}

function gainPerDay(
  previousKg: number | null,
  newKg: number,
  lastDate: string | null | undefined,
): string | null {
  const days = daysSince(lastDate);
  if (previousKg == null || days == null || days === 0) return null;
  const gain = ((newKg - previousKg) / days).toFixed(2);
  return `${gain} kg/d`;
}

export function BatchEventPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { propertyId } = usePropertyScope();
  const initialLotId = searchParams.get('lote') ?? '';
  const initialType = searchParams.get('tipo') ?? 'weighing';

  const [eventType, setEventType] = useState(
    BATCH_EVENT_TYPES.some((t) => t.value === initialType) ? initialType : 'weighing',
  );
  const [lotId, setLotId] = useState(initialLotId);
  const [eventDate, setEventDate] = useState(todayIso);
  const [responsibleName, setResponsibleName] = useState('');
  const [notes, setNotes] = useState('');
  const [bullId, setBullId] = useState('');
  const [isPregnant, setIsPregnant] = useState('true');
  const [treatmentKind, setTreatmentKind] = useState('vaccination');
  const [productName, setProductName] = useState('');
  const [dose, setDose] = useState('');
  const [route, setRoute] = useState('');
  const [productBatch, setProductBatch] = useState('');
  const [nextDoseDate, setNextDoseDate] = useState('');
  const [milkingShift, setMilkingShift] = useState('morning');
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const config = BATCH_EVENT_TYPES.find((t) => t.value === eventType) ?? BATCH_EVENT_TYPES[0];
  const { data: lots } = useLots(propertyId);
  const numericLotId = lotId ? Number(lotId) : undefined;

  const { data: animals, isLoading } = useAnimals({
    property_id: propertyId ?? undefined,
    lot_id: numericLotId,
    per_page: 500,
    sex: config.animalFilter === 'female' ? 'female' : undefined,
  });

  const { data: bulls } = useAnimals({
    property_id: propertyId ?? undefined,
    per_page: 500,
    sex: 'male',
  });

  const registerBatch = useRegisterBatchEvent();

  useEffect(() => {
    if (!animals?.animals) {
      setRows([]);
      return;
    }
    setRows((prev) => {
      const byId = new Map(prev.map((r) => [r.animal.id, r]));
      return animals.animals.map((animal) => {
        const existing = byId.get(animal.id);
        return {
          animal,
          weight: existing?.weight ?? '',
          included: existing?.included ?? false,
        };
      });
    });
  }, [animals?.animals]);

  const selectedCount = useMemo(() => {
    if (config.rowFields.includes('include')) {
      return rows.filter((r) => r.included).length;
    }
    return rows.filter((r) => r.weight.trim() !== '' && !Number.isNaN(Number(r.weight))).length;
  }, [rows, config]);

  function updateWeight(index: number, value: string) {
    setRows((list) => list.map((row, i) => (i === index ? { ...row, weight: value } : row)));
  }

  function toggleIncluded(index: number) {
    setRows((list) =>
      list.map((row, i) => (i === index ? { ...row, included: !row.included } : row)),
    );
  }

  function moveRow(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    setRows((list) => {
      const next = [...list];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!propertyId || !numericLotId) {
      setError('Selecione um lote.');
      return;
    }

    if (config.sessionFields?.includes('bull_id') && !bullId) {
      setError('Selecione o touro / sêmen da sessão.');
      return;
    }

    if (config.sessionFields?.includes('product_name') && !productName.trim()) {
      setError('Informe o produto da sessão.');
      return;
    }

    if (config.sessionFields?.includes('milking_shift') && !milkingShift) {
      setError('Selecione o turno da sessão.');
      return;
    }

    let items: { animal_id: number; weight_kg?: number; production_liters?: number }[] = [];

    if (config.rowFields.includes('include')) {
      items = rows
        .filter((r) => r.included)
        .map((r) => ({
          animal_id: r.animal.id,
          ...(r.weight.trim() !== '' && !Number.isNaN(Number(r.weight))
            ? eventType === 'milk_weighing'
              ? { production_liters: Number(r.weight) }
              : { weight_kg: Number(r.weight) }
            : {}),
        }));
    } else {
      items = rows
        .filter((r) => r.weight.trim() !== '')
        .map((r) =>
          eventType === 'milk_weighing'
            ? {
                animal_id: r.animal.id,
                production_liters: Number(r.weight),
              }
            : {
                animal_id: r.animal.id,
                weight_kg: Number(r.weight),
              },
        )
        .filter((r) => {
          const v = 'production_liters' in r ? r.production_liters : r.weight_kg;
          return !Number.isNaN(v ?? NaN) && (v ?? 0) >= 0;
        });
    }

    if (items.length === 0) {
      setError(
        config.rowFields.includes('include')
          ? 'Marque pelo menos um animal.'
          : 'Preencha o peso de pelo menos um animal.',
      );
      return;
    }

    try {
      const result = await registerBatch.mutateAsync({
        event_type: eventType,
        property_id: propertyId,
        event_date: eventDate,
        lot_id: numericLotId,
        responsible_name: responsibleName || null,
        notes: notes || null,
        bull_id: bullId ? Number(bullId) : undefined,
        is_pregnant:
          config.sessionFields?.includes('is_pregnant') ? isPregnant === 'true' : undefined,
        treatment_kind: config.sessionFields?.includes('treatment_kind')
          ? treatmentKind
          : undefined,
        product_name: config.sessionFields?.includes('product_name')
          ? productName.trim()
          : undefined,
        dose: dose.trim() || undefined,
        route: route || undefined,
        product_batch: productBatch.trim() || undefined,
        next_dose_date: nextDoseDate || undefined,
        milking_shift: config.sessionFields?.includes('milking_shift') ? milkingShift : undefined,
        items,
      });
      setSuccess(result.message);
      setRows((list) =>
        list.map((r) => ({
          ...r,
          weight: '',
          included: false,
        })),
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao registrar eventos.'));
    }
  }

  const lotOptions = (lots?.lots ?? [])
    .filter((l) => l.is_active)
    .map((l) => ({ value: l.id, label: l.name }));

  const bullOptions = (bulls?.animals ?? []).map((a) => ({
    value: a.id,
    label: `${a.internal_number}${a.animal_name ? ` — ${a.animal_name}` : ''}`,
  }));

  const showWeightColumn = config.rowFields.includes('weight_kg');
  const showIncludeColumn = config.rowFields.includes('include');

  return (
    <div>
      <PageHeader
        title="Registro em lote"
        subtitle="Registre o mesmo tipo de evento para vários animais de um lote"
      />

      {!propertyId ? (
        <Card>
          <CardBody className="py-10 text-center text-manejai-bark/60">
            Selecione uma <strong>fazenda ativa</strong> no topo.
          </CardBody>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Card>
            <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Select
                label="Tipo de evento"
                required
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                options={BATCH_EVENT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              />
              <Select
                label="Lote"
                required
                value={lotId}
                onChange={(e) => setLotId(e.target.value)}
                placeholder="Selecione o lote…"
                options={lotOptions}
              />
              <DateInput
                label="Data"
                required
                value={eventDate}
                onChange={setEventDate}
              />
              <Input
                label="Responsável"
                value={responsibleName}
                onChange={(e) => setResponsibleName(e.target.value)}
                placeholder="Nome de quem realizou o manejo"
              />
              {config.sessionFields?.includes('bull_id') && (
                <Select
                  label="Touro / sêmen (sessão)"
                  required
                  value={bullId}
                  onChange={(e) => setBullId(e.target.value)}
                  placeholder="Selecione…"
                  options={bullOptions}
                />
              )}
              {config.sessionFields?.includes('is_pregnant') && (
                <Select
                  label="Resultado (sessão)"
                  value={isPregnant}
                  onChange={(e) => setIsPregnant(e.target.value)}
                  options={[
                    { value: 'true', label: 'Prenhe' },
                    { value: 'false', label: 'Vazia' },
                  ]}
                />
              )}
              {config.sessionFields?.includes('treatment_kind') && (
                <Select
                  label="Tipo"
                  required
                  value={treatmentKind}
                  onChange={(e) => setTreatmentKind(e.target.value)}
                  options={[
                    { value: 'vaccination', label: 'Vacina' },
                    { value: 'medication', label: 'Medicamento' },
                  ]}
                />
              )}
              {config.sessionFields?.includes('product_name') && (
                <Input
                  label="Produto (sessão)"
                  required
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Ex.: Aftosa, Ivermectina…"
                />
              )}
              {config.sessionFields?.includes('dose') && (
                <Input
                  label="Dose"
                  value={dose}
                  onChange={(e) => setDose(e.target.value)}
                  placeholder="Ex.: 5 ml"
                />
              )}
              {config.sessionFields?.includes('route') && (
                <Select
                  label="Via"
                  value={route}
                  onChange={(e) => setRoute(e.target.value)}
                  placeholder="Opcional"
                  options={[
                    { value: 'im', label: 'IM' },
                    { value: 'sc', label: 'SC' },
                    { value: 'iv', label: 'IV' },
                    { value: 'oral', label: 'Oral' },
                    { value: 'topical', label: 'Tópica' },
                    { value: 'other', label: 'Outra' },
                  ]}
                />
              )}
              {config.sessionFields?.includes('product_batch') && (
                <Input
                  label="Lote do frasco"
                  value={productBatch}
                  onChange={(e) => setProductBatch(e.target.value)}
                  placeholder="Opcional"
                />
              )}
              {config.sessionFields?.includes('next_dose_date') && (
                <DateInput
                  label="Próxima dose"
                  value={nextDoseDate}
                  onChange={setNextDoseDate}
                />
              )}
              {config.sessionFields?.includes('milking_shift') && (
                <Select
                  label="Turno"
                  required
                  value={milkingShift}
                  onChange={(e) => setMilkingShift(e.target.value)}
                  options={MILKING_SHIFT_OPTIONS}
                />
              )}
              <div className="sm:col-span-2 lg:col-span-3">
                <Input
                  label="Observações da sessão"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex.: Manejo sanitário do lote"
                />
              </div>
              {config.hint && (
                <p className="sm:col-span-2 lg:col-span-3 text-xs text-manejai-bark/60">
                  {config.hint}
                </p>
              )}
            </CardBody>
          </Card>

          {!lotId ? (
            <Card>
              <CardBody className="py-12 text-center text-manejai-bark/60">
                Escolha um lote para carregar os animais.
              </CardBody>
            </Card>
          ) : isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 rounded-xl bg-manejai-sand/40 animate-pulse" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <Card>
              <EmptyState
                icon={ClipboardList}
                title="Lote sem animais"
                description={
                  config.animalFilter === 'female'
                    ? 'Não há fêmeas ativas neste lote.'
                    : 'Não há animais ativos neste lote.'
                }
                action={
                  <Button type="button" variant="outline" onClick={() => navigate('/lotes')}>
                    Ver lotes
                  </Button>
                }
              />
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-manejai-bark/70">
                <span>
                  {rows.length} animal(is) · {selectedCount} selecionado(s)
                </span>
                {showWeightColumn && (
                  <span className="text-xs">Use Tab para avançar entre os campos de peso</span>
                )}
              </div>

              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/60">
                        <th className="px-3 py-3 font-medium w-16">Ordem</th>
                        {showIncludeColumn && (
                          <th className="px-3 py-3 font-medium w-14">Incluir</th>
                        )}
                        <th className="px-3 py-3 font-medium">Cod. Interno</th>
                        <th className="px-3 py-3 font-medium">Nome</th>
                        {showWeightColumn && eventType === 'weighing' && (
                          <>
                            <th className="px-3 py-3 font-medium">Peso anterior</th>
                            <th className="px-3 py-3 font-medium">Última pesagem</th>
                          </>
                        )}
                        {showWeightColumn && (
                          <th className="px-3 py-3 font-medium w-36">
                            {eventType === 'milk_weighing'
                              ? 'Litros'
                              : `Peso (kg)${eventType === 'weaning' ? ' — opcional' : ''}`}
                          </th>
                        )}
                        {showWeightColumn && eventType === 'weighing' && (
                          <th className="px-3 py-3 font-medium">Ganho</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-manejai-sand/60">
                      {rows.map((row, index) => {
                        const newKg = row.weight.trim() ? Number(row.weight) : null;
                        const gain =
                          eventType === 'weighing' && newKg != null && !Number.isNaN(newKg)
                            ? gainPerDay(
                                row.animal.current_weight_kg,
                                newKg,
                                row.animal.last_weighing_date,
                              )
                            : null;

                        return (
                          <tr key={row.animal.id} className="hover:bg-manejai-cream/40">
                            <td className="px-3 py-2">
                              <div className="flex gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => moveRow(index, -1)}
                                  disabled={index === 0}
                                  className="p-1 rounded hover:bg-manejai-sand/60 disabled:opacity-30"
                                  aria-label="Mover para cima"
                                >
                                  <ArrowUp className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveRow(index, 1)}
                                  disabled={index === rows.length - 1}
                                  className="p-1 rounded hover:bg-manejai-sand/60 disabled:opacity-30"
                                  aria-label="Mover para baixo"
                                >
                                  <ArrowDown className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                            {showIncludeColumn && (
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={row.included}
                                  onChange={() => toggleIncluded(index)}
                                  className="h-4 w-4 rounded border-manejai-sand text-manejai-forest"
                                />
                              </td>
                            )}
                            <td className="px-3 py-2 font-medium text-manejai-forest">
                              {row.animal.internal_number}
                            </td>
                            <td className="px-3 py-2 text-manejai-bark">
                              {row.animal.animal_name ?? '—'}
                            </td>
                            {showWeightColumn && eventType === 'weighing' && (
                              <>
                                <td className="px-3 py-2">{formatWeight(row.animal.current_weight_kg)}</td>
                                <td className="px-3 py-2 text-manejai-bark/70">
                                  {formatDate(row.animal.last_weighing_date)}
                                </td>
                              </>
                            )}
                            {showWeightColumn && (
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={row.weight}
                                  onChange={(e) => updateWeight(index, e.target.value)}
                                  className="w-full h-9 rounded-lg border border-manejai-sand px-2 text-sm focus:outline-none focus:ring-2 focus:ring-manejai-sage"
                                  placeholder="—"
                                />
                              </td>
                            )}
                            {showWeightColumn && eventType === 'weighing' && (
                              <td className="px-3 py-2 text-xs text-manejai-moss">{gain ?? '—'}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              <p className="text-xs text-manejai-bark/50">
                Parto não está disponível em lote — cada registro exige dados do bezerro(a).
              </p>

              {error && <p className="text-sm text-red-600">{error}</p>}
              {success && <p className="text-sm text-emerald-700">{success}</p>}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => navigate('/eventos')}>
                  Cancelar
                </Button>
                <Button type="submit" loading={registerBatch.isPending} disabled={selectedCount === 0}>
                  Salvar {selectedCount} evento(s)
                </Button>
              </div>
            </>
          )}
        </form>
      )}
    </div>
  );
}
